import React, { useState } from "react";
import { Box, ButtonBase, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddRounded from "@mui/icons-material/AddRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import isNil from "lodash/isNil";
import { useForm, useFormState } from "react-final-form";
import Flx from "../../../components/layout/Flx";
import FormGrid from "../../../components/finalForm/FormGrid";
import Txt from "../../../components/typography/Txt";
import RffEmailField from "../../../components/finalForm/inputs/RffEmailField";
import RffPhoneField from "../../../components/finalForm/inputs/RffPhoneField";
import RffTextField from "../../../components/finalForm/inputs/RffTextField";
import { APP_TOKENS } from "../../../components/publicForm/publicFormTokens";
import { LOAN_CONTACT_ROLES } from "../applicationFormShared";

const CONTACT_FIELDS = ["firstName", "lastName", "emailAddress", "phone"];

const hasAnyValue = (contact) =>
  CONTACT_FIELDS.some((key) => {
    const v = contact?.[key];
    return !isNil(v) && v !== "";
  });

/**
 * The three fixed contact slots, as add-on-demand cards.
 *
 * Each role stays pinned to its array index — `loanRole` is stamped on at submit
 * time from LOAN_CONTACT_ROLES by position, so a role's card must always write
 * to loanRelatedContacts[its own index] even when the roles before it are empty.
 * That's why this isn't a FieldArray with push/remove: removing index 0 would
 * silently retitle every contact below it.
 */
const LoanContactsSection = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });

  // A slot is open if the applicant opened it this session, or if it already
  // holds data (a restored draft, or a back-navigation to this step).
  const [openRoles, setOpenRoles] = useState(() =>
    LOAN_CONTACT_ROLES.reduce((acc, role, i) => {
      acc[i] = hasAnyValue(values?.loanRelatedContacts?.[i]);
      return acc;
    }, {}),
  );

  const open = (index) => setOpenRoles((prev) => ({ ...prev, [index]: true }));

  const close = (index) => {
    // Clear the slot's fields so a closed card can't submit stray data — the
    // submit-time filter drops fully-empty contacts.
    CONTACT_FIELDS.forEach((key) =>
      form.change(`loanRelatedContacts[${index}].${key}`, undefined),
    );
    setOpenRoles((prev) => ({ ...prev, [index]: false }));
  };

  return (
    <Flx column gap={1.5}>
      {LOAN_CONTACT_ROLES.map((role, index) =>
        openRoles[index] ? (
          <ContactCard
            key={role}
            role={role}
            index={index}
            onClose={() => close(index)}
          />
        ) : (
          <AddContactButton key={role} role={role} onClick={() => open(index)} />
        ),
      )}
    </Flx>
  );
};

const AddContactButton = ({ role, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={(theme) => ({
      justifyContent: "flex-start",
      gap: 1,
      px: 2,
      py: 1.75,
      borderRadius: "10px",
      // borderStrong, not border: this is a control outline, and at 1px dashed
      // the lighter divider token barely reads as a box at all.
      border: `1px dashed ${APP_TOKENS.borderStrong}`,
      color: theme.palette.primary.main,
      transition: "border-color 160ms ease, background 160ms ease",
      "&:hover": {
        borderColor: theme.palette.primary.main,
        background: alpha(theme.palette.primary.main, 0.06),
      },
    })}
  >
    <AddRounded sx={{ fontSize: 18 }} />
    <Txt
      component="div"
      sx={{ fontSize: 14, fontWeight: 500, color: "inherit" }}
    >
      {`Add ${role}`}
    </Txt>
  </ButtonBase>
);

const ContactCard = ({ role, index, onClose }) => (
  <Box
    sx={{
      border: `1px solid ${APP_TOKENS.border}`,
      borderRadius: "10px",
      background: APP_TOKENS.surface,
      p: 2,
    }}
  >
    <Flx between centerY sx={{ mb: 1.5 }}>
      <Txt
        component="div"
        sx={{ fontWeight: 700, fontSize: 13.5, color: APP_TOKENS.ink }}
      >
        {role}
      </Txt>
      <IconButton size="small" onClick={onClose} aria-label={`Remove ${role}`}>
        <CloseRounded sx={{ fontSize: 16, color: APP_TOKENS.inkMuted }} />
      </IconButton>
    </Flx>
    {/* Fixed two columns. FlxGrid's auto-fit packed as many 180px tracks as
        fitted, which made three across and stranded Phone alone on row two. */}
    <FormGrid columns={2} gap={2}>
      <RffTextField
        size={1}
        name={`loanRelatedContacts[${index}].firstName`}
        label="First Name"
      />
      <RffTextField
        size={1}
        name={`loanRelatedContacts[${index}].lastName`}
        label="Last Name"
      />
      <RffEmailField
        size={1}
        name={`loanRelatedContacts[${index}].emailAddress`}
        label="Email"
      />
      <RffPhoneField
        size={1}
        name={`loanRelatedContacts[${index}].phone`}
        label="Phone"
      />
    </FormGrid>
  </Box>
);

export default LoanContactsSection;
